import { type FC, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { type Note } from './NotesModal';
import { Button } from 'layout/Button';
import { FDatePickerField } from '@components/form/FDatePickerField';
import { TextAreaInput } from '@components/inputs/TextAreaInput';
import { FLabel } from '@components/form/FLabel';

interface NoteFormProps {
  note?: Note | null;
  onSave: (
    note: Omit<Note, 'id' | 'created_by_user' | 'created_at' | 'updated_at'>
  ) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface NoteFormData {
  date: Date;
  description: string;
}

export const NoteForm: FC<NoteFormProps> = ({
  note,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const methods = useForm<NoteFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      date: new Date(),
      description: '',
    },
  });

  const { watch, formState } = methods;
  const description = watch('description') || '';
  const characterCount = description.length;
  const isFormValid = formState.isValid;

  useEffect(() => {
    if (note) {
      methods.reset({
        date: new Date(note.date),
        description: note.description,
      });
    } else {
      const now = new Date();
      methods.reset({
        date: now,
        description: '',
      });
    }
  }, [note, methods]);

  const handleSave = (data: NoteFormData): void => {
    const formattedData: Omit<
      Note,
      'id' | 'created_by_user' | 'created_at' | 'updated_at'
    > = {
      date: data.date.toISOString(),
      description: data.description.trim(),
    };
    onSave(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <form
        className="w-full"
        onSubmit={(e) => {
          void methods.handleSubmit(handleSave)(e);
        }}
      >
        <div className="p-4 border rounded-lg w-full">
          <div className="flex flex-col gap-4">
            <FDatePickerField
              name="date"
              label="Date"
              maxDate={new Date()}
              disabled={isLoading}
            />
            <div>
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex flex-col gap-1.5">
                  <FLabel>Note</FLabel>
                </div>
                <Controller
                  name="description"
                  control={methods.control}
                  rules={{
                    validate: (value: string) => {
                      const trimmed = value?.trim() || '';
                      if (!trimmed) {
                        return 'Note cannot be empty';
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextAreaInput
                      {...field}
                      placeholder="Write your note here..."
                      rows={4}
                      className="resize-none"
                      draggable={false}
                      maxLength={255}
                      disabled={isLoading}
                      error={error}
                    />
                  )}
                />
              </div>
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {characterCount}/255
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                onClick={onCancel}
                label="Cancel"
                variant="secondary"
                disabled={isLoading}
              />
              <Button
                type="submit"
                label={note ? 'Save' : 'Add'}
                variant="primary"
                isLoading={isLoading}
                disabled={isLoading || !isFormValid}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
